/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type DesktopTileNode, MAX_TILE_SPLIT_CHILDREN } from '@poweralpha/common';

/*
 * Pure operations on the tile tree: splits lay their children out in a row or column by weight;
 * leaves are windows or free slots. Nothing here knows about window state.
 */

export type TileNode = DesktopTileNode;
type TileLeaf = Extract<TileNode, { kind: 'window' | 'slot' }>;
export type TileAxis = 'row' | 'column';
export type TileSide = 'left' | 'right' | 'top' | 'bottom';
export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where a dragged window lands, mirroring macOS/Windows snapping plus splitting a tile. */
export type TileDropTarget =
  | { kind: 'maximize' }
  | { kind: 'edge'; side: 'left' | 'right' | 'bottom' }
  | { kind: 'corner'; x: 'left' | 'right'; y: 'top' | 'bottom' }
  | { kind: 'leaf'; id: string; side: TileSide | 'center' };

/** A divider between two siblings; dragging it moves their shared edge. */
export interface TileSeam {
  /** Child indexes from the root to the split. */
  path: number[];
  /** The seam sits after this child. */
  index: number;
  axis: TileAxis;
  rect: Rect;
  /** The split's size along its axis, which its weights divide. */
  extent: number;
}

/** Identifies a divider across layouts: the split's path and the child it follows. */
export const seamKey = ({ path, index }: Pick<TileSeam, 'path' | 'index'>) =>
  `${path.join('.')}:${index}`;

export const TILE_GAP = 10;
export const MIN_TILE_SIZE = 200;
/** Keep captured pointers just outside the desktop useful (toolbar, gutter and dock). */
const OUTSIDE_TOLERANCE_PX = 24;
/** The outer quarter is a broad half-screen target; its upper/lower quarters target corners. */
const SIDE_REGION_FRACTION = 0.25;
const CORNER_FRACTION = 0.25;
/** Top/bottom center regions maximize or tile to the bottom half. The center stays free. */
const VERTICAL_REGION_FRACTION = 0.12;
/** Outer share of a tile on each side that splits it; a slot's middle fills it. */
const TILE_EDGE_FRACTION = 0.2;

const isLeaf = (node: TileNode): node is TileLeaf => node.kind !== 'split';
const split = (
  axis: TileAxis,
  children: TileNode[],
  weights = children.map(() => 1),
): TileNode => ({
  kind: 'split',
  axis,
  weights,
  children,
});

export function tileLeaves(node: TileNode | undefined): TileLeaf[] {
  if (!node) return [];
  return isLeaf(node) ? [node] : node.children.flatMap(tileLeaves);
}

export const tiledWindowIds = (node: TileNode | undefined) =>
  tileLeaves(node).flatMap((leaf) => (leaf.kind === 'window' ? [leaf.id] : []));

/** Leaf rects and the seams between siblings, inside `rect`. */
export function layoutTiles(node: TileNode | undefined, rect: Rect, gap = TILE_GAP) {
  const leaves = new Map<string, Rect>();
  const seams: TileSeam[] = [];
  const visit = (current: TileNode, area: Rect, path: number[]) => {
    if (isLeaf(current)) {
      leaves.set(current.id, area);
      return;
    }
    const row = current.axis === 'row';
    const start = row ? area.x : area.y;
    const available = (row ? area.width : area.height) - gap * (current.children.length - 1);
    const total = current.weights.reduce((sum, weight) => sum + weight, 0);
    let offset = start;
    current.children.forEach((child, index) => {
      const size = (available * current.weights[index]) / total;
      const from = Math.round(offset);
      const to = Math.round(offset + size);
      visit(
        child,
        row
          ? { x: from, y: area.y, width: to - from, height: area.height }
          : { x: area.x, y: from, width: area.width, height: to - from },
        [...path, index],
      );
      offset += size + gap;
      if (index < current.children.length - 1)
        seams.push({
          path,
          index,
          axis: current.axis,
          extent: row ? area.width : area.height,
          rect: row
            ? { x: to, y: area.y, width: gap, height: area.height }
            : { x: area.x, y: to, width: area.width, height: gap },
        });
    });
  };
  if (node) visit(node, rect, []);
  return { leaves, seams };
}

/** The tree without the leaf; a split left with one child collapses into it. */
export function removeLeaf(node: TileNode | undefined, id: string): TileNode | undefined {
  if (!node) return undefined;
  if (isLeaf(node)) return node.id === id ? undefined : node;
  const kept = node.children.flatMap((child, index) => {
    const next = removeLeaf(child, id);
    return next ? [{ child: next, weight: node.weights[index] }] : [];
  });
  if (kept.length === 0) return undefined;
  if (kept.length === 1) return kept[0].child;
  if (
    kept.length === node.children.length &&
    kept.every(({ child }, i) => child === node.children[i])
  )
    return node;
  return split(
    node.axis,
    kept.map(({ child }) => child),
    kept.map(({ weight }) => weight),
  );
}

/** Vacate a maximized tile without changing its neighbours' rectangles. */
export function vacateLeaf(node: TileNode | undefined, id: string): TileNode | undefined {
  if (!node) return node;
  const used = new Set(tileLeaves(node).map((leaf) => leaf.id));
  let index = 1;
  while (used.has(`slot-${index}`)) index++;
  return mapLeaf(node, id, () => ({ kind: 'slot', id: `slot-${index}` }));
}

const mapLeaf = (node: TileNode, id: string, map: (leaf: TileLeaf) => TileNode): TileNode => {
  if (isLeaf(node)) return node.id === id ? map(node) : node;
  return { ...node, children: node.children.map((child) => mapLeaf(child, id, map)) };
};

/** Splits leaf `id` so `added` takes half of it on `side`. */
const splitLeaf = (node: TileNode, id: string, side: TileSide, added: TileNode): TileNode => {
  const axis: TileAxis = side === 'left' || side === 'right' ? 'row' : 'column';
  const before = side === 'left' || side === 'top';
  if (isLeaf(node))
    return node.id === id ? split(axis, before ? [added, node] : [node, added]) : node;
  const index = node.children.findIndex((child) => isLeaf(child) && child.id === id);
  // Splitting along the parent's own axis adds a sibling instead of nesting a split, unless the
  // parent is full (the session schema caps a split's children).
  if (index !== -1 && node.axis === axis && node.children.length < MAX_TILE_SPLIT_CHILDREN) {
    const at = before ? index : index + 1;
    const half = node.weights[index] / 2;
    const weights = node.weights.map((weight, i) => (i === index ? half : weight));
    return {
      ...node,
      children: node.children.toSpliced(at, 0, added),
      weights: weights.toSpliced(at, 0, half),
    };
  }
  return { ...node, children: node.children.map((child) => splitLeaf(child, id, side, added)) };
};

/**
 * Snapping a window replaces free space an earlier snap left unfilled (repeated snaps would
 * otherwise pile up empty slots and squeeze the windows). Filling a slot keeps the others.
 */
const withoutStaleSlots = (layout: TileNode | undefined, target: TileDropTarget) => {
  if (target.kind === 'leaf' && target.side === 'center') return layout;
  const keep = target.kind === 'leaf' ? target.id : undefined;
  return tileLeaves(layout).reduce<TileNode | undefined>(
    (node, leaf) => (leaf.kind === 'slot' && leaf.id !== keep ? removeLeaf(node, leaf.id) : node),
    layout,
  );
};

/** The tree after window `id` is dropped on `target`; new free space becomes slots. */
export function dropOnTiles(
  layout: TileNode | undefined,
  target: Exclude<TileDropTarget, { kind: 'maximize' }>,
  id: string,
): TileNode {
  const pruned = withoutStaleSlots(layout, target);
  // New slots never reuse a pruned slot's id, so they read as new (and get the app picker).
  const used = new Set(tileLeaves(layout).map((leaf) => leaf.id));
  const slot = (): TileNode => {
    let n = 1;
    while (used.has(`slot-${n}`)) n++;
    used.add(`slot-${n}`);
    return { kind: 'slot', id: `slot-${n}` };
  };
  const window: TileLeaf = { kind: 'window', id };
  const rest = removeLeaf(pruned, id);
  if (target.kind === 'leaf') {
    if (!rest) return window;
    // A slot's middle fills it; near an edge, a tile or slot splits.
    return target.side === 'center'
      ? mapLeaf(rest, target.id, () => window)
      : splitLeaf(rest, target.id, target.side, window);
  }
  if (target.kind === 'edge') {
    const other = rest ?? slot();
    if (target.side === 'bottom') return split('column', [other, window]);
    return split('row', target.side === 'left' ? [window, other] : [other, window]);
  }
  const column = split('column', target.y === 'top' ? [window, slot()] : [slot(), window]);
  const other = rest ?? slot();
  return split('row', target.x === 'left' ? [column, other] : [other, column]);
}

/** Replaces the weights of the split at `path`. */
export function setSplitWeights(node: TileNode, path: number[], weights: number[]): TileNode {
  if (isLeaf(node)) return node;
  if (path.length === 0) return { ...node, weights };
  const [index, ...rest] = path;
  return {
    ...node,
    children: node.children.map((child, i) =>
      i === index ? setSplitWeights(child, rest, weights) : child,
    ),
  };
}

const findSplit = (node: TileNode, path: number[]): Extract<TileNode, { kind: 'split' }> | null => {
  let current: TileNode | undefined = node;
  for (const index of path)
    current = current?.kind === 'split' ? current.children[index] : undefined;
  return current?.kind === 'split' ? current : null;
};

/**
 * Weights after dragging the seam after child `index` by `delta` px: only its two neighbours
 * change, and neither shrinks below the minimum tile size.
 */
export function moveSeam(
  node: TileNode,
  { path, index, extent }: Pick<TileSeam, 'path' | 'index' | 'extent'>,
  delta: number,
): number[] | null {
  const target = findSplit(node, path);
  if (!target) return null;
  const total = target.weights.reduce((sum, weight) => sum + weight, 0);
  const pxPerWeight = (extent - TILE_GAP * (target.children.length - 1)) / total;
  const pair = target.weights[index] + target.weights[index + 1];
  const pairPx = pair * pxPerWeight;
  // Each side keeps room for every window inside it, however deeply it nests.
  const firstMin = minExtent(target.children[index], target.axis);
  const secondMin = minExtent(target.children[index + 1], target.axis);
  if (pairPx < firstMin + secondMin) return null;
  const firstPx = Math.min(
    pairPx - secondMin,
    Math.max(firstMin, target.weights[index] * pxPerWeight + delta),
  );
  return target.weights.toSpliced(
    index,
    2,
    firstPx / pxPerWeight,
    (pairPx - firstPx) / pxPerWeight,
  );
}

/** The least room a node needs along `axis` for each of its tiles to keep the minimum size. */
function minExtent(node: TileNode, axis: TileAxis): number {
  if (isLeaf(node)) return MIN_TILE_SIZE;
  const mins = node.children.map((child) => minExtent(child, axis));
  if (node.axis !== axis) return Math.max(...mins);
  // Inner weights stay fixed while an outer divider moves, so each child needs its minimum at its
  // own share of the split.
  const total = node.weights.reduce((sum, weight) => sum + weight, 0);
  const shared = Math.max(...mins.map((min, i) => (min * total) / node.weights[i]));
  return shared + TILE_GAP * (mins.length - 1);
}

/** Whether every tile of `layout` keeps the minimum size inside `bounds`. */
export const tilesFit = (layout: TileNode, bounds: Rect) =>
  [...layoutTiles(layout, bounds).leaves.values()].every(
    ({ width, height }) => width >= MIN_TILE_SIZE && height >= MIN_TILE_SIZE,
  );

/** What a window dragged with the pointer at `point` (desktop coordinates) would snap to. */
export function findDropTarget({
  point,
  bounds,
  leaves,
  draggedId,
}: {
  point: { x: number; y: number };
  bounds: { width: number; height: number };
  leaves: ReadonlyMap<string, Rect>;
  draggedId: string;
}): TileDropTarget | null {
  const { width, height } = bounds;
  const { x } = point;
  if (x < -OUTSIDE_TOLERANCE_PX || x > width + OUTSIDE_TOLERANCE_PX) return null;
  // Below the desktop (over the dock, down to the browser's edge) still means its bottom edge.
  const y = Math.min(point.y, height);
  // Filling an offered slot keeps the rest of an existing arrangement intact, even where
  // the slot overlaps a desktop snap region.
  const hit = [...leaves].find(
    ([id, rect]) =>
      id !== draggedId &&
      x >= rect.x &&
      x <= rect.x + rect.width &&
      y >= rect.y &&
      y <= rect.y + rect.height,
  );
  const leaf = hit ? leafTarget(point, hit) : null;
  if (leaf?.kind === 'leaf' && leaf.side === 'center') return leaf;
  const sideWidth = width * SIDE_REGION_FRACTION;
  if (x <= sideWidth || x >= width - sideWidth) {
    const side = x <= sideWidth ? 'left' : 'right';
    if (y <= height * CORNER_FRACTION) return { kind: 'corner', x: side, y: 'top' };
    if (y >= height * (1 - CORNER_FRACTION)) return { kind: 'corner', x: side, y: 'bottom' };
    return { kind: 'edge', side };
  }
  if (y <= height * VERTICAL_REGION_FRACTION) return { kind: 'maximize' };
  if (y >= height * (1 - VERTICAL_REGION_FRACTION)) return { kind: 'edge', side: 'bottom' };
  return leaf;
}

/**
 * Near a tile's edge splits it. The middle of a slot fills it; the middle of a tile is open
 * desktop, so a tile dragged there floats free.
 */
const leafTarget = (
  { x, y }: { x: number; y: number },
  [id, rect]: [string, Rect],
): TileDropTarget | null => {
  const fx = (x - rect.x) / rect.width;
  const fy = (y - rect.y) / rect.height;
  const distances: [TileSide, number][] = [
    ['left', fx],
    ['right', 1 - fx],
    ['top', fy],
    ['bottom', 1 - fy],
  ];
  const [side, distance] = distances.reduce((a, b) => (b[1] < a[1] ? b : a));
  if (distance <= TILE_EDGE_FRACTION) return { kind: 'leaf', id, side };
  return id.startsWith('slot-') ? { kind: 'leaf', id, side: 'center' } : null;
};
